import {
	useLoaderData,
	useLocation,
	useNavigation,
	useSearchParams,
} from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import PlentyBody from '#app/components/typography/plenty-body'
import { type ClerkFacet } from '#app/constants/clerk-api'
import { type loader } from '#app/routes/_public+/products+/$slug'
import { cn } from '#app/utils/misc'
import { mainColorsMapped } from '../filters/colors-utils'
import AppliedFilterLabel from './label'

export interface AppliedFilter {
	key: string
	value: string
	isColor?: boolean
	isSize?: boolean
	isGender?: string
	isMaterials?: boolean
}

interface AppliedFiltersProps {
	withoutReset?: boolean
}

const AppliedFilters = ({ withoutReset = false }: AppliedFiltersProps) => {
	const { facetsData } = useLoaderData<typeof loader>()
	const [, setSearchParams] = useSearchParams()
	const navigation = useNavigation()
	const location = useLocation()
	const { t } = useTranslation('product_list_page')

	const filterParams = new URLSearchParams(
		navigation.state === 'idle' ? location.search : navigation.location?.search,
	)

	let hasFilterParams = false

	filterParams.forEach((_, key) => {
		if (
			key === 'color' ||
			key === 'size' ||
			key === 'brand' ||
			key === 'gender' ||
			key === 'category' ||
			key === 'materials'
		) {
			hasFilterParams = true
		}
	})

	const resetFilters = () => {
		// Get the current search parameters
		const currentParams = new URLSearchParams(location.search)
		// Create a new URLSearchParams object to store the filtered parameters
		const newParams = new URLSearchParams()

		// Iterate through the current parameters
		currentParams.forEach((value, key) => {
			// Keep only the parameters with the key "store" or "query"
			if (key === 'store' || key === 'query') {
				newParams.append(key, value)
			}
		})

		// Set the new filtered parameters
		setSearchParams(newParams)
	}

	const appliedFilters = getFiltersFromParams(
		filterParams,
		facetsData?.facets?.sizes,
		facetsData?.facets?.materials,
		t,
	)

	return (
		<div
			className={cn(
				'flex flex-row flex-wrap gap-2 transition-all',
				appliedFilters.length > 0 ? 'mt-2 h-fit' : 'mt-0 h-0',
				withoutReset && appliedFilters.length > 0 ? 'mt-6' : '',
			)}
		>
			{appliedFilters.map(filter => {
				return (
					<AppliedFilterLabel
						key={`${filter.key}-${filter.value}`}
						appliedFilter={filter}
					/>
				)
			})}

			{hasFilterParams && !withoutReset && (
				<div
					onClick={resetFilters}
					className="group flex cursor-pointer flex-col items-center self-center transition-all hover:bg-transparent"
				>
					<PlentyBody size="md">{t('search:reset_filters')}</PlentyBody>
					<div className="flex w-full flex-row justify-center">
						<div className="h-[4px] w-0 rotate-0 rounded-sm bg-mint-green-80p transition-all before:w-0 group-hover:w-[4px] group-focus:w-full"></div>
					</div>
				</div>
			)}
		</div>
	)
}

function getFiltersFromParams(
	params: URLSearchParams,
	sizesFacets: ClerkFacet[],
	materialsFacets: ClerkFacet[],
	t: (key: string) => string,
): AppliedFilter[] {
	const selectedFilters: AppliedFilter[] = []
	params.forEach((value, key) => {
		if (
			key === 'color' ||
			key === 'size' ||
			key === 'brand' ||
			key === 'gender' ||
			key === 'category' ||
			key === 'materials'
		) {
			switch (key) {
				case 'color':
					const mainColor = mainColorsMapped.find(
						facet => facet.name.en === value || facet.name['da'] === value,
					)
					if (mainColor)
						selectedFilters.push({ key, value, isColor: true, isSize: false })
					break
				case 'size':
					// @todo the value should be inside the facets data
					sizesFacets.find(facet => facet.value === value) &&
						selectedFilters.push({ key, value, isColor: false, isSize: true })
					break
				case 'gender':
					if (value !== 'Unisex') {
						const convertedGender =
							value === 'Herre' || value === 'Male'
								? t('gender_boy')
								: t('gender_girl')
						selectedFilters.push({
							key,
							value: value,
							isColor: false,
							isSize: false,
							isGender: convertedGender,
						})
					}
					break
				case 'category':
					selectedFilters.push({
						key: `${key}-${value}`,
						isColor: false,
						isSize: false,
						value: t(`categories:${value.toLowerCase()}`),
					})
					break
				case 'materials':
					const material = materialsFacets.find(
						facet => facet.value === value || facet.terms?.includes(value),
					)
					if (
						!selectedFilters.find(
							filter =>
								filter.value === material?.value ||
								filter.value === material?.value,
						)
					)
						selectedFilters.push({
							key,
							value: material?.value || value,
							isColor: false,
							isSize: false,
							isMaterials: true,
						})
					break
				default:
					selectedFilters.push({
						key,
						value,
						isColor: false,
						isSize: false,
					})
					break
			}
		}
	})
	return selectedFilters
}

export default AppliedFilters
